import { useMutation } from '@apollo/client'
import { createComment } from '@faceup/crypto'
import { FormattedMessage, sharedMessages, useIntl } from '@faceup/localization'
import { type Attachment, Form, Prompt, twMerge } from '@faceup/ui'
import { Alert, Button, Checkbox, useNotification } from '@faceup/ui-base'
import { CaseCreationOrigin, CaseStatusGroup, Plan } from '@faceup/utils'
import { type ComponentProps, type ReactElement, type ReactNode, useContext, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import type { ChatInput } from '../ChatInput'
import { ChatPopupAlertType, PopupsContext } from './PopupsContext'
import { RelativeMessagesContext } from './RelativeMessagesContext'

const mutations = {
  SendMessageMutation: graphql(`
    mutation SendMessageMutation(
      $input: CreateCompanyReportFollowUpCommentInput!
      $isVictim: Boolean!
    ) {
      createCompanyReportFollowUpComment(input: $input) {
        createdCompanyReportComment {
          id
          case {
            id
            updatedAt
            lastChangeDate @include(if: $isVictim)
            assignedMembers @skip(if: $isVictim) {
              id
            }
          }
        }
      }
    }
  `),

  EditCompanyReportNameVisibility: graphql(`
    mutation EditCompanyReportNameVisibilityMutation(
      $input: EditCompanyReportNameVisibilityInput!
    ) {
      editCompanyReportNameVisibility(input: $input) {
        case {
          id
          isNameHidden
        }
      }
    }
  `),
}

const fragments = {
  CompanyChatForm_case: graphql(`
    fragment CompanyChatForm_case on CaseInterface {
      id
      isDemo
      isNameHidden
      ... on CompanyReport {
        status {
          id
          groupName
        }
      }
      encryptionKey
      creationOrigin
      organizationalUnit {
        id
        mother {
          id
        }
        billing {
          id
          plan
        }
      }
      victim {
        id
      }
    }
  `),
}

type ChatFormProps = {
  report: FragmentType<typeof fragments.CompanyChatForm_case>
  isLoading?: boolean
  userId: string
  sendClicked?: () => void
  input: (
    props: { forceDisableSubmitButton: (state: boolean) => void } & Pick<
      ComponentProps<typeof ChatInput>,
      'disabled' | 'value' | 'onChange' | 'attachments'
    >
  ) => ReactNode
  _wrapWithPermissionTooltip?: (content: (hasPermissions: boolean) => ReactElement) => ReactElement
}

export const ChatForm = ({
  report: _report,
  userId,
  sendClicked,
  input,
  isLoading,
  _wrapWithPermissionTooltip = content => <>{content(true)}</>,
}: ChatFormProps) => {
  const report = getFragmentData(fragments.CompanyChatForm_case, _report)
  const relativeMessages = useContext(RelativeMessagesContext)
  const { formatMessage } = useIntl()
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [isSubmitButtonForciblyDisabled, setIsSubmitButtonForciblyDisabled] =
    useState<boolean>(false)
  const { alertType } = useContext(PopupsContext)
  const [text, setText] = useState('')
  const notification = useNotification()

  const disabled =
    !report?.isDemo &&
    report?.__typename === 'CompanyReport' &&
    (report?.status?.groupName === CaseStatusGroup.Closed || !report?.victim?.id)
  const emptyInput = !text.trim().length && !attachments.length
  const allowAttachments =
    report?.organizationalUnit?.billing?.plan === Plan.Basic ||
    report?.organizationalUnit?.billing?.plan === Plan.Standard ||
    report?.organizationalUnit?.billing?.plan === Plan.Premium

  const [sendMessage, { loading: loadingSendMessage }] = useMutation(
    mutations.SendMessageMutation,
    {
      onCompleted: () => {
        setText('')
        setAttachments([])
      },
    }
  )

  const caseId = report?.id ?? null
  const motherId = report?.organizationalUnit?.mother?.id ?? report?.organizationalUnit?.id

  const [toggleHideName] = useMutation(mutations.EditCompanyReportNameVisibility, {
    variables: {
      input: {
        caseId,
      },
    },
  })

  const isUserVictim = Boolean(report?.victim?.id) && userId === report?.victim?.id

  return (
    <Form
      style={{ textAlign: 'end', width: '100%' }}
      data-test='send-message-form'
      onSubmit={async () => {
        if (isSubmitButtonForciblyDisabled) {
          return
        }
        if (text.trim().length || attachments.length) {
          const payload = await createComment(
            text.trim(),
            report?.encryptionKey ?? '',
            attachments.map(({ file }) => file)
          )

          if (payload.isErr()) {
            console.error(payload.error)
            notification.error({
              message: formatMessage(sharedMessages.encryptionError),
            })
            return
          }

          sendClicked?.()
          await sendMessage({
            variables: {
              input: {
                motherId,
                caseId: report?.id ?? '',
                ...payload.value,
              },
              isVictim: isUserVictim,
            },
          })
        }
      }}
    >
      <Form.Item>
        {alertType === ChatPopupAlertType.NotVictimReport && !report?.isDemo ? (
          <Alert
            className='border-0 bg-colorInfoBg text-start'
            message={
              relativeMessages.type === 'Admin' && (
                <FormattedMessage message={relativeMessages.functionalityUnsupported} />
              )
            }
            type='info'
          />
        ) : (
          input({
            forceDisableSubmitButton: setIsSubmitButtonForciblyDisabled,
            value: text,
            onChange: setText,
            disabled,
            attachments: allowAttachments
              ? {
                  attachments,
                  setAttachments,
                  hookAttachmentsToDelete: null,
                }
              : null,
          })
        )}
      </Form.Item>
      {relativeMessages.type === 'Victim' && (
        <Prompt
          when={text.length > 0}
          message={formatMessage(relativeMessages.leaveQuestion)}
          title={formatMessage(relativeMessages.leaveQuestionTitle)}
        />
      )}
      <ChatFooter>
        {!isUserVictim && report?.creationOrigin !== CaseCreationOrigin.Member ? (
          _wrapWithPermissionTooltip(hasPermissions => (
            <Checkbox
              checked={report?.isNameHidden ?? false}
              onChange={() => toggleHideName()}
              disabled={!hasPermissions}
            >
              <span className='text-colorTextSecondary'>
                {relativeMessages.type === 'Admin' && (
                  <FormattedMessage message={relativeMessages.hideMyName} />
                )}
              </span>
            </Checkbox>
          ))
        ) : (
          <div />
        )}
        <Button
          loading={loadingSendMessage || isLoading}
          type='primary'
          htmlType='submit'
          disabled={disabled || emptyInput || isSubmitButtonForciblyDisabled}
          data-test='submit-chat-message-button'
        >
          <FormattedMessage message={sharedMessages.send} />
        </Button>
      </ChatFooter>
    </Form>
  )
}

/**
 * Visual shell of <ChatForm /> with a disabled input and send button.
 * Used as a placeholder while the wrapper's queries are loading so the
 * layout doesn't shift once data arrives.
 */
export const ChatFormPlaceholder = ({ input }: Pick<ChatFormProps, 'input'>) => (
  <Form style={{ textAlign: 'end', width: '100%' }}>
    <Form.Item>
      {input({
        forceDisableSubmitButton: () => null,
        value: '',
        onChange: () => null,
        disabled: true,
        attachments: null,
      })}
    </Form.Item>
    <ChatFooter>
      <div />
      <Button type='primary' htmlType='submit' disabled>
        <FormattedMessage message={sharedMessages.send} />
      </Button>
    </ChatFooter>
  </Form>
)

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const ChatFooter = ({ className, ...props }: ComponentProps<'div'>) => (
  <div className={twMerge('flex items-center justify-between', className)} {...props} />
)
